import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Avatar,
  Icons,
  Tooltip,
  useTheme,
  Text,
} from '@ublend-npm/aulaui-core';
import debounce from 'lodash.debounce';
import Fuse from 'fuse.js';
import uniqBy from 'lodash.uniqby';

interface Space {
  id: string;
  name: string;
  year?: string;
  archived?: boolean | null;
  isStarred: boolean;
  hasActivity: boolean;
  avatar?: string;
  categories: { label: string; order?: number }[];
}

interface SpacesSearchProps {
  spaces: Space[];
  onSpaceSelect: (
    id: string,
    showArchived: boolean,
    sort: string,
    category: string | null,
    subCategory?: string | null
  ) => void;
  onSpaceStar: (args: {
    spaceId: string;
    showArchived: boolean;
    sort: string;
    search: string;
    category: string | null;
    subCategory?: string | null;
  }) => Promise<void>;
  search: string;
  showArchived: boolean;
  sort: 'az' | 'za';
  onfetchRecentlyViewedSpaces: () => void;
}

const SpacesSearch: React.FC<SpacesSearchProps> = ({
  spaces,
  onSpaceSelect,
  onSpaceStar,
  search,
  showArchived,
  sort,
  onfetchRecentlyViewedSpaces,
}) => {
  const [filteredSpaces, setFilteredSpaces] = useState<Space[]>([]);
  const [fuse, setFuse] = useState<Fuse<Space, Fuse.FuseOptions<Space>>>();
  const theme = useTheme();
  // Replace Record<string, boolean> with a plain object type
  const [savingStars, setSavingStars] = useState<{ [key: string]: boolean }>(
    {}
  );

  const sortedSpaces = useMemo(() => {
    const list = showArchived ? [...spaces] : spaces.filter((s) => !s.archived);
    return list.sort((a, b) =>
      sort === 'az'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );
  }, [spaces, sort, showArchived]);

  const updateFiltered = useCallback(
    (query: string) => {
      if (!fuse || !query) {
        setFilteredSpaces(sortedSpaces);
        return;
      }

      const fuseResults = fuse
        .search(query)
        .map((r) => r.item)
        .filter((item) => item && (showArchived || !item.archived));

      const directMatches = spaces.filter(
        (space) =>
          space &&
          (showArchived || !space.archived) &&
          space.name?.toLowerCase().includes(query.toLowerCase())
      );

      const uniqueResults = uniqBy([...directMatches, ...fuseResults], 'id');

      const sorted = uniqueResults.sort((a, b) =>
        sort === 'az'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );

      setFilteredSpaces(sorted);
    },
    [fuse, showArchived, sort, spaces, sortedSpaces]
  );

  const debouncedSearch = useMemo(
    () => debounce(updateFiltered, 200),
    [updateFiltered]
  );

  useEffect(() => {
    setFuse(
      new Fuse(spaces, {
        keys: ['name'],
        threshold: 0.3,
        shouldSort: true,
      })
    );
  }, [spaces]);

  useEffect(() => {
    if (!search) {
      debouncedSearch.cancel();
      setFilteredSpaces(sortedSpaces);
    } else {
      debouncedSearch(search);
    }
  }, [search, sortedSpaces]);

  const [starOverrides, setStarOverrides] = useState<{
    [key: string]: boolean;
  }>({});

  // Clear optimistic star overrides when spaces data updates,
  // so star/unstar actions from other sources stay in sync with search results.
  useEffect(() => {
    setStarOverrides({});
  }, [spaces]);

  const handleStarToggle = async (space: Space) => {
    if (!space?.id) return;

    const currentStarred = starOverrides[space.id] ?? space.isStarred;
    const nextStarred = !currentStarred;

    setStarOverrides((prev) => ({ ...prev, [space.id]: nextStarred }));
    setSavingStars((prev) => ({ ...prev, [space.id]: true }));

    try {
      await onSpaceStar({
        spaceId: space.id,
        showArchived,
        sort,
        search,
        category: null,
      });
    } finally {
      setSavingStars((prev) => ({ ...prev, [space.id]: false }));
      onfetchRecentlyViewedSpaces();
    }
  };

  const SHOW_TOOLTIP_THRESHOLD = 17;

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box width="100%">
        {search.length >= 1 && (
          <Card
            variant="outlined"
            sx={{
              p: theme.spacing(2),
              maxHeight: theme.spacing(55), // 440px
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h6"
              sx={{ m: '8px 0px 16px' }}
              tabIndex={0}
              aria-label={`Search result ${filteredSpaces.length}`}
            >
              Search results ({filteredSpaces.length})
            </Typography>
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {filteredSpaces.map((space) => {
                const showTooltip = space.name.length >= SHOW_TOOLTIP_THRESHOLD;

                const rowContent = (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1}
                    py={1.5}
                    borderRadius={theme.shape.borderRadius * 0}
                    sx={{
                      cursor: 'pointer',
                      [theme.breakpoints.up('sm')]: {
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      flex={1}
                      minWidth={0}
                      onClick={() =>
                        onSpaceSelect(space.id, showArchived, sort, null)
                      }
                      tabIndex={0}
                      role="button"
                      data-testid="space-search-item"
                      aria-label={`Switch to ${space.name} space`}
                    >
                      <Avatar
                        variant="circular"
                        src={space.avatar}
                        fallbackText={space.name?.charAt(0).toUpperCase()}
                        showBadge={space.hasActivity}
                        sx={{
                          width: theme.spacing(5),
                          height: theme.spacing(5),
                          flexShrink: 0,
                        }}
                        badgeColor={theme.palette.primary.main}
                      >
                        {space.name?.[0] ?? '?'}
                      </Avatar>

                      <Box overflow="hidden" sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Text fontWeight={500} maxLines={2}>
                          {space.name}
                        </Text>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            const categories = space.categories ?? [];
                            const sortedCategories = [...categories].sort(
                              (a, b) => (a.order ?? 0) - (b.order ?? 0)
                            );
                            const firstLabel = sortedCategories[0]?.label || '';
                            return `${firstLabel}${
                              space.archived ? ' (Archived)' : ''
                            }`;
                          })()}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStarToggle(space);
                      }}
                      aria-label={`${space.isStarred ? 'Unstar' : 'Star'} the ${
                        space.name
                      } space`}
                      disabled={!!savingStars[space.id]}
                      color="primary"
                      sx={{
                        width: '34px',
                        height: '34px',
                        '&:active': { transform: 'scale(0.9)' },
                      }}
                    >
                      {(starOverrides[space.id] ?? space.isStarred) ||
                      savingStars[space.id] ? (
                        <Icons.Star />
                      ) : (
                        <Icons.StarBorder />
                      )}
                    </IconButton>
                  </Box>
                );

                return (
                  <Box key={space.id}>
                    {showTooltip ? (
                      <Tooltip title={space.name} placement="top" arrow>
                        {/* Tooltip needs to wrap a single child */}
                        <Box>{rowContent}</Box>
                      </Tooltip>
                    ) : (
                      rowContent
                    )}
                  </Box>
                );
              })}
            </Box>
            {filteredSpaces.length === 0 && (
              <Typography
                variant="subtitle1"
                fontWeight={400}
                fontStyle="normal"
                fontSize="1rem" // 16px, which is standard for subtitle1
                lineHeight="175%"
                letterSpacing="0.15px"
                textAlign="center"
                color="text.secondary"
                // py={2}
                // px={2}
                tabIndex={0}
              >
                Your search didn’t match any of your spaces. Try with different
                keywords.
              </Typography>
            )}
          </Card>
        )}
      </Box>
    </Box>
  );
};

SpacesSearch.propTypes = {
  spaces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      year: PropTypes.string,
      archived: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
      isStarred: PropTypes.bool.isRequired,
      hasActivity: PropTypes.bool.isRequired,
      avatar: PropTypes.string,
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  onSpaceSelect: PropTypes.func.isRequired,
  onSpaceStar: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  showArchived: PropTypes.bool.isRequired,
  onfetchRecentlyViewedSpaces: PropTypes.func.isRequired,
  sort: PropTypes.oneOf(['az', 'za']).isRequired,
};

export default SpacesSearch;
